import Vue from 'vue'
import VueRouter from 'vue-router'
import { permissionForRoute, hasPermission } from '@/utils/permissions'
import projectAuth from '@/utils/projectAuth'
import auth from '@/utils/auth'
import { getAuthContext } from '@/api/admin'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    meta: { title: '登录', public: true },
    component: () => import('../views/Login.vue')
  },
  {
    path: '/',
    name: 'Workbench',
    meta: { title: '工作台' },
    component: () => import('../views/Home.vue')
  },

  // ── 巡场配置 ──
  {
    path: '/config/plans',
    name: 'PatrolPlans',
    meta: { title: '巡场计划', activeMenu: '/config/plans' },
    component: () => import('../views/config/PlanListPage.vue')
  },
  {
    path: '/config/plans/:guid/edit',
    name: 'PlanEditor',
    meta: { title: '编辑巡场计划', activeMenu: '/config/plans' },
    component: () => import('../views/config/PlanEditorPage.vue')
  },
  {
    path: '/config/questions',
    name: 'QuestionBank',
    meta: { title: '标准题库', activeMenu: '/config/questions' },
    component: () => import('../views/config/QuestionBankPage.vue')
  },
  {
    path: '/config/datasources',
    name: 'Datasources',
    meta: { title: '数据源', activeMenu: '/config/datasources' },
    component: () => import('../views/config/DatasourcePage.vue')
  },

  // ── 排班与任务 ──
  {
    path: '/schedule/list',
    name: 'ScheduleList',
    meta: { title: '排班管理', activeMenu: '/schedule/list' },
    component: () => import('../views/schedule/SchedulePage.vue')
  },
  {
    path: '/schedule/calendar',
    name: 'ScheduleCalendar',
    meta: { title: '排班总览', activeMenu: '/schedule/calendar' },
    component: () => import('../views/schedule/SchedulePage.vue')
  },
  {
    path: '/schedule/special',
    name: 'ScheduleSpecial',
    meta: { title: '特别检查', activeMenu: '/schedule/special' },
    component: () => import('../views/schedule/SchedulePage.vue')
  },

  // ── 巡场结果 ──
  {
    path: '/result/daily',
    name: 'DailyManage',
    meta: { title: '巡场日报', activeMenu: '/result/daily' },
    component: () => import('../views/report/DailyPage.vue')
  },
  {
    path: '/result/daily/:patrolReportGuid',
    name: 'DailyReportDetail',
    meta: { title: '日报详情', activeMenu: '/result/daily' },
    component: () => import('../views/report/DailyReportDetail.vue')
  },
  {
    path: '/result/issues',
    name: 'IssueManage',
    meta: { title: '问题工单', activeMenu: '/result/issues' },
    component: () => import('../views/report/IssuesPage.vue')
  },
  {
    path: '/result/vision-records',
    name: 'VisionRecords',
    meta: { title: '图片识别记录', activeMenu: '/result/vision-records' },
    component: () => import('../views/report/VisionRecordsPage.vue')
  },

  // ── 系统设置 ──
  {
    path: '/settings/projects',
    name: 'ProjectManage',
    meta: { title: '项目管理', activeMenu: '/settings/projects' },
    component: () => import('../views/settings/ProjectPage.vue')
  },
  {
    path: '/settings/staff',
    name: 'StaffManage',
    meta: { title: '人员管理', activeMenu: '/settings/staff' },
    component: () => import('../views/settings/StaffPage.vue')
  },
  {
    path: '/settings/roles',
    name: 'RoleManage',
    meta: { title: '角色权限', activeMenu: '/settings/roles' },
    component: () => import('../views/settings/RolePage.vue')
  },
  {
    path: '/settings/notifications',
    name: 'Notifications',
    meta: { title: '预警与提醒', activeMenu: '/settings/notifications' },
    component: () => import('../views/settings/NotificationsPage.vue')
  },
  {
    path: '/settings/ai',
    name: 'AiConfig',
    meta: { title: 'AI 配置', activeMenu: '/settings/ai' },
    component: () => import('../views/settings/AiConfigPage.vue')
  },
  {
    path: '/settings/vision-import',
    name: 'VisionImport',
    meta: { title: '导入历史问题', activeMenu: '/settings/vision-import' },
    component: () => import('../views/settings/VisionImportPage.vue')
  },
  {
    path: '/settings/assistant-rules',
    name: 'AssistantRules',
    meta: { title: '助手话术', activeMenu: '/settings/assistant-rules' },
    component: () => import('../views/settings/AssistantRulesPage.vue')
  },
  {
    path: '/settings/automation',
    redirect: { path: '/settings/notifications', query: { tab: 'tasks' } }
  },

  // ── 旧路径重定向 ──
  { path: '/master/template', redirect: '/config/plans' },
  { path: '/master/question-bank', redirect: '/config/questions' },
  { path: '/master/staff', redirect: '/settings/staff' },
  { path: '/master/role', redirect: '/settings/roles' },
  { path: '/report/daily', redirect: '/result/daily' },
  { path: '/report/issues', redirect: '/result/issues' },
  { path: '/system/tasks', redirect: { path: '/settings/notifications', query: { tab: 'tasks' } } },
  { path: '/system/sla', redirect: '/config/plans' },
  { path: '/system/duty-manager', redirect: '/config/plans' },
  { path: '/settings/policy', redirect: '/config/plans' },
  { path: '/admin/config', redirect: '/config/plans' },
  { path: '/admin/master-data', redirect: '/config/questions' },
  { path: '/admin/scheduled-tasks', redirect: { path: '/settings/notifications', query: { tab: 'tasks' } } },
  { path: '/admin/staff', redirect: '/settings/staff' },
  { path: '/admin/permission', redirect: '/settings/roles' },
  { path: '/admin/projects', redirect: '/settings/projects' },
  { path: '/dm/config', redirect: '/config/plans' },
  { path: '/dm/schedule', redirect: '/schedule/list' },
  { path: '/dm/daily', redirect: '/result/daily' },
  { path: '/dm/question', redirect: '/result/issues' }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) return next()
  if (!auth.isLoggedIn()) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }
  const need = permissionForRoute(to.path)
  if (!need) return next()
  if (!projectAuth.getContext()) {
    try { await projectAuth.loadContext(getAuthContext) } catch { /* offline */ }
  }
  if (!hasPermission(need)) {
    next({ path: '/', replace: true })
  } else {
    next()
  }
})

export default router
